import { Injectable } from '@angular/core';

export interface JwtPayload {
  sub: string; // username
  roles: string[]; // user roles
  iat: number; // issued at
  exp: number; // expiration
}

@Injectable({
  providedIn: 'root',
})
export class JwtHelperService {
  /**
   * Decode JWT token payload
   * @param token JWT token string
   * @returns Decoded payload or null if invalid
   */
  decodeToken(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = this.base64UrlDecode(payload);
      return JSON.parse(decoded) as JwtPayload;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   * @param token JWT token string
   * @returns true if expired, false otherwise
   */
  isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  }

  /**
   * Get roles from token
   * @param token JWT token string
   * @returns Array of role strings or empty array
   */
  getRoles(token: string): string[] {
    const payload = this.decodeToken(token);
    return payload?.roles || [];
  }

  /**
   * Get username from token
   * @param token JWT token string
   * @returns Username or null
   */
  getUsername(token: string): string | null {
    const payload = this.decodeToken(token);
    return payload?.sub || null;
  }

  /**
   * Check if user has specific role
   * @param token JWT token string
   * @param role Role to check (e.g., 'ROLE_ADMIN')
   * @returns true if user has role
   */
  hasRole(token: string, role: string): boolean {
    const roles = this.getRoles(token);
    return roles.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   * @param token JWT token string
   * @param roles Roles to check
   * @returns true if user has at least one of the roles
   */
  hasAnyRole(token: string, roles: string[]): boolean {
    const userRoles = this.getRoles(token);
    return roles.some((role) => userRoles.includes(role));
  }

  /**
   * Base64 URL decode (JWT uses URL-safe Base64)
   * @param str Base64 URL encoded string
   * @returns Decoded string
   */
  private base64UrlDecode(str: string): string {
    // Replace URL-safe characters
    let output = str.replace(/-/g, '+').replace(/_/g, '/');

    // Pad with '=' to make length multiple of 4
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        throw new Error('Invalid base64url string');
    }

    // Decode base64
    return decodeURIComponent(
      atob(output)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
  }
}
